import {
  addClass,
  removeClass,
  toggleClass,
  toggleAttribute,
  slideDownTogglableSection,
} from '../../assets/js/utility';

class TuitionCalc {
  constructor() {
    this.urlQuery = new URLSearchParams(window.location.search);
    this.toggleClass = 'is-open'; // match accordion-item
    this.panelStatusAttribute = 'data-item-status'; // complete, current, pending, calculated
    this.questionValueAttribute = 'data-selected-value-text';
    this.valueMapToMainAttr = 'data-mapto-definition-value';
    this.dataLabelAttr = 'data-label';
    this.highlightsFallbackIconAttr = 'data-fallback-icon';
    this.valuePropertyName = 'value';
    this.altValuePropertyName = 'valueAlt';

    // container targets
    this.resultsPanelClassname = 'tuitionCalcResults';
    this.panelItemClass = 'simple-accordion-item';
    this.groupInteractiveElementClass = 'accordion-item-heading';
    this.highlightListClass = 'highlights';
    this.highlightIconClass = 'highlight-icon';
    this.highlightLabelClass = 'highlight-label';
    this.highlightTotalClass = 'highlight-total';
    this.overviewTotalClass = 'overview-total';
    this.overviewTotalAmountClass = 'amount';
    this.breakdownClass = 'itemized-breakdown';
    this.breakdownItemLabelClass = 'cost-def-label';
    this.breakdownItemDescriptionClass = 'cost-def-desc';
    this.breakdownItemAmountClass = 'cost-def-amount';

    this.calculators = document.querySelectorAll('.section-tuition-calc');
    this.calcRadios = document.querySelectorAll(
      '.simple-calc-panel-radio-group input'
    );
    this.calcSelects = document.querySelectorAll(
      '.simple-calc-panel-selectbox select'
    );

    // util
    this.currencyFormat = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    });

    // data -- this would be for *all* calcs on the page
    this.allOptionsJson = [];

    if (this.calculators && this.calculators.length > 0) {
      document.querySelectorAll('script.calc-panel-json').forEach((script) => {
        this.allOptionsJson.push(JSON.parse(script.textContent));
      });

      if (Object.keys(this.allOptionsJson).length === 0) {
        console.error('Unable to acquire panel json data for calculators.');
        return;
      }

      for (const tuitionCalc of this.calculators) {
        let calcPanels = tuitionCalc.querySelectorAll(
          '.calc-panels .simple-accordion-item'
        );

        if (calcPanels && calcPanels.length > 0) {
          this.initCalcPanels(calcPanels);
        }

        // init breakdown show details button
        let showDetailsButton = tuitionCalc.querySelector('.is-expand-prompt');
        if (showDetailsButton) {
          showDetailsButton.addEventListener('click', (e) => {
            let spanLabel = showDetailsButton.querySelector('span');
            let breakdownArea = e.target.closest('.breakdown');
            if (!breakdownArea) {
              return;
            }

            slideDownTogglableSection(e, '.breakdown', '.itemized-breakdown');
            toggleAttribute(showDetailsButton, 'aria-expanded');
            toggleClass(breakdownArea, this.toggleClass);
            toggleClass(showDetailsButton, this.toggleClass); // button styles required for arrow state

            spanLabel.textContent =
              showDetailsButton.getAttribute('aria-expanded') === 'true'
                ? showDetailsButton.getAttribute('data-hide-label')
                : showDetailsButton.getAttribute('data-show-label');
          });
        }
      }

      if (this.calcRadios) {
        for (const radioButton of this.calcRadios) {
          radioButton.addEventListener('change', (e) => {
            let panelItem = e.target.closest(`.${this.panelItemClass}`);
            let panelButton =
              panelItem != null
                ? panelItem.querySelector(`.${this.groupInteractiveElementClass}`)
                : null;

            // need the associated label text to assign to panel title
            let optionText = e.target.closest('label')
              ? e.target.closest('label').innerText
              : '';

            this.updatePanelSelectedValue(panelItem, panelButton, optionText);
            this.setNextPanel(panelItem.nextElementSibling);
          });
        }
      }

      if (this.calcSelects) {
        for (const selectbox of this.calcSelects) {
          selectbox.addEventListener('change', (e) => {
            let panelItem = e.target.closest(`.${this.panelItemClass}`);
            let panelButton =
              panelItem != null
                ? panelItem.querySelector(`.${this.groupInteractiveElementClass}`)
                : null;

            // need the associated label text to assign to panel title
            let optionText = e.target.selectedOptions[0].textContent;

            this.updatePanelSelectedValue(panelItem, panelButton, optionText);
            this.setNextPanel(panelItem.nextElementSibling);
          });
        }
      }
    }
  }

  initCalcPanels(calcPanels) {
    // init state based on any url query
    if (this.urlQuery != '') {
      for (const [key, value] of this.urlQuery.entries()) {
        for (const panel of calcPanels) {
          let interactableElement = panel.querySelector('button');

          if (!interactableElement) {
            console.warn('Error finding button for query entry.');
            continue;
          }

          if (interactableElement.id.toLowerCase() != key.toLowerCase()) {
            continue;
          }

          // check is a valid value
          const isRadioGroup = panel.querySelector('[role=radiogroup');
          const qTypeLabels = isRadioGroup
            ? panel.querySelectorAll('.option-chip span')
            : panel.querySelectorAll('option');

          if (!qTypeLabels) {
            console.error('Issue finding panel options on query initialization.');
            continue;
          }

          // if so, set the associated user input option value & update the panel
          for (const element of qTypeLabels) {
            if (
              decodeURI(value).toLowerCase() != element.textContent.toLowerCase()
            ) {
              continue;
            }

            if (isRadioGroup) {
              let radioElement = element
                .closest('.option-chip')
                .querySelector('input');
              radioElement.checked = true;
            } else {
              element.selected = true;
            }

            interactableElement.disabled = false;
            this.updatePanelSelectedValue(
              panel,
              interactableElement,
              element.textContent
            );
            break;
          }
        }
      }
    }

    // user starts at first panel that is not marked as complete
    this.setNextPanel(calcPanels[0]);
  }

  /**
   * Updates the visible panel heading to include text for what the user selected
   * @param {Node Element} panel - the accordion item itself
   * @param {Node Element} interactableElement - the accordion heading button element
   * @param {string} value - text to add to the interactableElement
   * @returns
   */
  updatePanelSelectedValue(panel, interactableElement, value) {
    if (!panel || !interactableElement) {
      console.error(
        'The panel or interactable element is invalid for updating panel selected value.'
      );
      return;
    }

    // don't animate if not open
    if (interactableElement.getAttribute('aria-expanded') == 'true') {
      slideDownTogglableSection(
        interactableElement,
        `.${this.panelItemClass}`,
        '.content'
      );
    }

    removeClass(panel, this.toggleClass);
    panel.setAttribute(this.panelStatusAttribute, 'complete');
    interactableElement.setAttribute('aria-expanded', false);
    interactableElement.setAttribute(this.questionValueAttribute, value.trim());
  }

  /**
   * Sets the new state of the given panel and updates the next panel's state accordingly
   * @param {Node Element} panelElement - the accordion item
   * @returns
   */
  setNextPanel(panelElement) {
    if (!panelElement) {
      console.error('Panel Element is null for setting state.');
      return false;
    }

    let newCurrentPanelSet = false;
    let panelButton = panelElement.querySelector('button');

    if (!panelButton) {
      console.warn('Error finding button when setting panel.');
      return;
    }

    switch (panelElement.getAttribute(this.panelStatusAttribute)) {
      case 'complete':
        // already complete, try to find next pending or current
        let nextPanel = panelElement.nextElementSibling;
        while (
          nextPanel &&
          !nextPanel.classList.contains(this.resultsPanelClassname)
        ) {
          if (nextPanel.getAttribute(this.panelStatusAttribute) != 'complete') {
            break;
          }

          nextPanel = nextPanel.nextElementSibling;
        }

        // carry forward set status
        newCurrentPanelSet = this.setNextPanel(nextPanel);
        break;

      case 'current':
        // shouldn't have to do anything but make sure it's expanded
        addClass(panelElement, this.toggleClass);
        panelButton.setAttribute('aria-expanded', true);
        break;

      case 'pending':
        panelElement.setAttribute(this.panelStatusAttribute, 'current');
        newCurrentPanelSet = true;

        addClass(panelElement, this.toggleClass);
        panelButton.setAttribute('aria-expanded', true);
        panelButton.disabled = false;
        break;

      default:
        // results panel; ensure it's open and interactable
        addClass(panelElement, this.toggleClass);
        panelElement.setAttribute(this.panelStatusAttribute, 'calculated');
        panelButton.setAttribute('aria-expanded', true);
        panelButton.disabled = false;

        let calculatorContainer = panelElement.closest('.section-tuition-calc');

        if (calculatorContainer) {
          this.populateResults(calculatorContainer);
        }
        break;
    }

    return newCurrentPanelSet;
  }

  // //////////////////////////////
  // BEGIN RESULTS PANEL FUNCTIONS

  /**
   * Gets the data-attribute that will be used for calculations according
   * to any selected value mapping applied
   * @param {Node Element} valueMapPanel
   * @returns
   */
  getActiveValueAttributeFromMapping(valueMapPanel) {
    if (!valueMapPanel) {
      console.warn(
        'Invalid value map panel provided to get mapped attribute. Using fallback.'
      );
      // default to main attribute
      return this.valuePropertyName;
    }

    // panel should have either a radio group OR a select and it should only have
    // two options as the associated cost definition items only have 2 value options
    const selectedRadio = valueMapPanel.querySelector('input[type="radio"]:checked');
    if (selectedRadio) {
      return selectedRadio.getAttribute(this.dataLabelAttr).toLowerCase() ==
        valueMapPanel.getAttribute(this.valueMapToMainAttr).toLowerCase()
        ? this.valuePropertyName
        : this.altValuePropertyName;
    }

    const selectbox = valueMapPanel.querySelector('select');
    if (selectbox) {
      return selectbox.options[selectbox.selectedIndex]
        .getAttribute(this.dataLabelAttr)
        .toLowerCase() ==
        valueMapPanel.getAttribute(this.valueMapToMainAttr).toLowerCase()
        ? this.valuePropertyName
        : this.altValuePropertyName;
    }

    // default to main attribute
    return this.valuePropertyName;
  }

  /**
   * Gathers all the selected inputs of type radio or select for calculation
   * @param {Node Element} calcContainer - calculator section element
   * @returns
   */
  findSelectedInputs(calcContainer) {
    let selectedInputIds = [];

    const checkedRadios = calcContainer.querySelectorAll(
      'input[type="radio"]:checked'
    );
    for (const radio of checkedRadios) {
      if (radio) {
        selectedInputIds.push(radio.value);
      }
    }

    const selects = calcContainer.querySelectorAll('select');
    for (const select of selects) {
      if (select && select.value !== '') {
        selectedInputIds.push(select.value);
      }
    }

    return selectedInputIds;
  }

  /**
   * Determines the 'effectiveValue' to use based on any value mapping
   * and sorts the data in descending order of effectiveValue.
   * Removes any entries that have an effectiveValue equal to 0.
   * @param {Object} optionMap - key-value pair of object data
   * @param {boolean} useAltValue - flag for whether to use the main value or alt value in json
   * @returns
   */
  sortedDataMap(optionMap, useAltValue = false) {
    const items = [];

    for (const option of Object.values(optionMap)) {
      const value = Number(option.value);
      const valueAlt = Number(option.valueAlt);
      if (isNaN(value) || isNaN(valueAlt)) {
        console.warn(
          `Value (${option.value}) or Alt Value (${option.valueAlt}) is not a valid number. Skipping.`
        );
        continue;
      }

      const effectiveValue = useAltValue && valueAlt !== 0 ? valueAlt : value;

      if (effectiveValue > 0) {
        items.push({
          ...option,
          value,
          valueAlt,
          effectiveValue,
        });
      }

      // some items, like programs, may have additional fees to consider
      // currently they're treated like top line items but have unvariable value
      if (Array.isArray(option.otherFees)) {
        for (const fee of option.otherFees) {
          const feeValue = Number(fee.value);
          if (isNaN(feeValue)) {
            console.warn(
              `Other fee ${fee.label} has an invalid value (${fee.value})`
            );
            continue;
          }

          if (feeValue > 0) {
            items.push({
              ...fee,
              effectiveValue: feeValue,
            });
          }
        }
      }
    }

    return items.sort((a, b) => b.effectiveValue - a.effectiveValue);
  }

  /**
   * Populates the highlights list from data list using the template
   * in the html
   * @param {Node Element} calcContainer - calculation section
   * @param {Array} highlightData - list of objects
   * @returns
   */
  populateHighlights(calcContainer, highlightData) {
    if (highlightData.length === 0) {
      console.error('Highlight data empty.');
      return;
    }

    let highlightsContainer = calcContainer.querySelector(
      `.${this.highlightListClass}`
    );
    if (!highlightsContainer) {
      console.warn('Unable to find highlights list element.');
      return;
    }

    let highlightTemplate = highlightsContainer.querySelector('template');
    if (!highlightTemplate) {
      console.warn('Unable to find the highlight list item template.');
      return;
    }

    // clear any existing clones
    for (const child of Array.from(highlightsContainer.children)) {
      if (child.tagName !== 'TEMPLATE') {
        highlightsContainer.removeChild(child);
      }
    }

    // clone template, populate, and insert into list
    for (const highlight of highlightData) {
      const clone = highlightTemplate.content.cloneNode(true);
      let icon = clone.querySelector(`.${this.highlightIconClass} > i`);
      let label = clone.querySelector(`.${this.highlightLabelClass}`);
      let total = clone.querySelector(`.${this.highlightTotalClass}`);

      if (highlight.icon == '') {
        highlight.icon = highlightsContainer.getAttribute(
          this.highlightsFallbackIconAttr
        );
      }
      highlight.icon.split(' ').forEach((cls) => icon.classList.add(cls));
      label.textContent = highlight.label;
      total.textContent = this.currencyFormat.format(highlight.effectiveValue);

      highlightsContainer.appendChild(clone);
    }
  }

  /**
   * Populates the breakdown row items from data list using the template
   * in the html
   * @param {Node Element} calcContainer - calculation section
   * @param {Array} lineItems - list of objects
   * @returns
   */
  populateBreakdownList(calcContainer, lineItems) {
    if (lineItems.length === 0) {
      console.error('Line items data empty.');
      return;
    }

    let breakdownTable = calcContainer.querySelector(
      `.${this.breakdownClass} tbody`
    );
    if (!breakdownTable) {
      console.warn('Unable to find breakdown table body element.');
      return;
    }

    let breakdownRowTemplate = breakdownTable.querySelector('template');
    if (!breakdownRowTemplate) {
      console.warn('Unable to find the itemsized row template.');
      return;
    }

    // clear any existing line items
    for (const child of Array.from(breakdownTable.children)) {
      if (child.tagName !== 'TEMPLATE') {
        breakdownTable.removeChild(child);
      }
    }

    // clone template, populate, and insert into table body
    for (const lineItem of lineItems) {
      const clone = breakdownRowTemplate.content.cloneNode(true);
      let thead = clone.querySelector('th');
      let label = clone.querySelector(`.${this.breakdownItemLabelClass}`);
      let description = clone.querySelector(
        `.${this.breakdownItemDescriptionClass}`
      );
      let amount = clone.querySelector(`.${this.breakdownItemAmountClass}`);

      thead.textContent = lineItem.label;
      label.textContent = lineItem.label;
      description.textContent = lineItem.description;
      amount.textContent = this.currencyFormat.format(lineItem.effectiveValue);

      breakdownTable.appendChild(clone);
    }
  }

  /**
   * Handles proc'ing updates for the results panel based on user selections
   * @param {Node Element} calculator - calculator section element
   */
  populateResults(calculator) {
    if (calculator == null) {
      console.error('Given calculator null when trying to calculate results.');
      return;
    }

    let valueMappingPanel = calculator.querySelector(`[${this.valueMapToMainAttr}]`);
    const useAltValue =
      this.getActiveValueAttributeFromMapping(valueMappingPanel) ==
      this.altValuePropertyName;
    const SELECTED_SET = new Set(this.findSelectedInputs(calculator));

    const standardFeesData = JSON.parse(
      calculator.querySelector('.tuitionCalcResults script.calc-panel-json')
        .textContent
    );

    // may be empty options
    if (standardFeesData.options.length > 0) {
      for (const fee of standardFeesData.options) {
        SELECTED_SET.add(fee.optionId);
      }
    }

    // pull the option data for just the selected ones + standard fees
    let allSelectedMap = {};
    for (const group of this.allOptionsJson) {
      // if found all the selected, don't keep looking
      if (Object.keys(allSelectedMap).length === SELECTED_SET.size) {
        break;
      }

      for (const option of group.options) {
        if (Object.keys(allSelectedMap).length === SELECTED_SET.size) {
          break;
        }

        if (SELECTED_SET.has(option.optionId)) {
          allSelectedMap[option.optionId] = option;
        }
      }
    }

    // value set with only the active value property we care about according
    // to user selection
    const sortedList = this.sortedDataMap(allSelectedMap, useAltValue);

    let overviewTotal = sortedList.reduce(
      (sum, item) => sum + item.effectiveValue,
      0
    );
    let overviewAmountElement = calculator.querySelector(
      `.${this.overviewTotalClass} .${this.overviewTotalAmountClass}`
    );
    overviewAmountElement.textContent = this.currencyFormat.format(overviewTotal);

    this.populateHighlights(calculator, sortedList.slice(0, 3));
    this.populateBreakdownList(calculator, sortedList);
  }
}

export default TuitionCalc;
